import { useAuth } from '@/backend/context/auth-management';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Clock, LogOut, MessageCircle, Zap } from 'lucide-react';
import { WhatsAppUtils } from '@/shared/utils';

export function ProcessingScreen() {
  const { logout, adminSettings, profile } = useAuth();

  const isSuspended = adminSettings?.accountStatus === 'suspended';
  const statusLabel = isSuspended ? 'Suspensión' : 'Expiración';

  const handleSupport = () => {
    const businessName = profile?.public?.branding?.name || 'Mi Negocio';
    WhatsAppUtils.redirectToContact(
      `Hola, realicé el pago de mi plan y estoy esperando la validación. Negocio: ${businessName}`,
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-zinc-950 p-6 text-center text-white">
      <div className="relative mb-10">
        <div
          className={cn(
            'absolute inset-0 animate-pulse rounded-full blur-3xl',
            isSuspended ? 'bg-rose-500/20' : 'bg-amber-500/20',
          )}
        />
        <div
          className={cn(
            'relative rounded-[2.5rem] p-8 ring-1',
            isSuspended
              ? 'bg-rose-500/10 ring-rose-500/50'
              : 'bg-amber-500/10 ring-amber-500/50',
          )}
        >
          <Clock
            className={cn(
              'animate-spin-slow h-16 w-16',
              isSuspended ? 'text-rose-500' : 'text-amber-500',
            )}
          />
        </div>
        <div className="absolute -top-2 -right-2">
          <div className="relative flex h-8 w-8">
            <span
              className={cn(
                'absolute inline-flex h-full w-full animate-ping rounded-full opacity-75',
                isSuspended ? 'bg-rose-400' : 'bg-amber-400',
              )}
            ></span>
            <span
              className={cn(
                'relative inline-flex h-8 w-8 items-center justify-center rounded-full',
                isSuspended ? 'bg-rose-500' : 'bg-amber-500',
              )}
            >
              <Zap className="h-4 w-4 fill-current text-zinc-950" />
            </span>
          </div>
        </div>
      </div>

      <h1 className="mb-4 text-4xl font-black tracking-tighter uppercase italic">
        Pago en{' '}
        <span className={isSuspended ? 'text-rose-500' : 'text-amber-500'}>
          Proceso
        </span>
      </h1>

      <p className="mb-12 max-w-xs text-sm leading-relaxed font-bold text-zinc-400 opacity-80">
        Estamos validando tu pago para resolver la {statusLabel.toLowerCase()}{' '}
        de tu cuenta. Este proceso suele tomar unos minutos.
      </p>

      <div className="grid w-full max-w-sm gap-4">
        <Button
          variant="outline"
          onClick={handleSupport}
          className="h-20 w-full rounded-[2rem] border-zinc-800 bg-zinc-900/50 text-lg font-black tracking-widest text-white uppercase shadow-2xl hover:bg-zinc-800"
        >
          <MessageCircle className="mr-3 h-6 w-6 text-emerald-400" />
          Contactar Soporte
        </Button>

        <button
          onClick={() => logout()}
          className={cn(
            'mt-4 flex items-center justify-center gap-2 text-[10px] font-black tracking-[0.3em] text-zinc-500 uppercase transition-all',
            isSuspended ? 'hover:text-rose-400' : 'hover:text-amber-400',
          )}
        >
          <LogOut className="h-4 w-4" />
          Cerrar Sesión
        </button>
      </div>

      <div className="absolute bottom-10 opacity-20">
        <img src="/logo.svg" alt="TuNomina" className="h-8 grayscale" />
      </div>

      <style
        dangerouslySetInnerHTML={{
          __html: `
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .animate-spin-slow {
          animation: spin-slow 8s linear infinite;
        }
      `,
        }}
      />
    </div>
  );
}
